import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Save, X, Loader2 } from 'lucide-react';
import { formatApiErrorDetail } from '@/utils/api';
import { SprintTimer } from '@/components/SprintTimer';
import { FeedbackWidget } from '@/components/FeedbackWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['romance', 'scifi', 'mystery', 'horror', 'fantasy', 'drama'];

const StoryEditor = ({ initialPrompt, onClearPrompt }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('romance');
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [wordCount, setWordCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialPrompt) {
      setPrompt(initialPrompt);
    }
  }, [initialPrompt]);

  useEffect(() => {
    const words = content.trim().split(/\s+/).filter((word) => word.length > 0);
    setWordCount(words.length);
  }, [content]);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      setError('Please provide both title and content');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post(
        `${API}/stories`,
        {
          title: title.trim(),
          content: content.trim(),
          prompt: prompt || null,
          category,
        },
        { withCredentials: true }
      );
      setSuccess(true);
      setSaveCount((c) => c + 1);
      setTitle('');
      setContent('');
      setPrompt('');
      if (onClearPrompt) onClearPrompt();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearPrompt = () => {
    setPrompt('');
    if (onClearPrompt) onClearPrompt();
  };

  return (
    <div className="space-y-6" data-testid="story-editor">
      <SprintTimer wordCount={wordCount} />
      {prompt && (
        <Card className="bg-amber-50 border-amber-200" data-testid="active-prompt">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900 mb-1">Using Prompt:</p>
                <p className="text-amber-700 italic">&ldquo;{prompt}&rdquo;</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClearPrompt}
                data-testid="clear-prompt-button"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded" data-testid="save-success">
          Story saved successfully!
        </div>
      )}
      {saveCount > 0 && <FeedbackWidget key={saveCount} context="story_saved" />}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" data-testid="save-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="story-title">Story Title</Label>
          <Input
            id="story-title"
            placeholder="Enter your story title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="story-title-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="story-category">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger id="story-category" data-testid="story-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="story-content">Your Story</Label>
          <span className="text-sm text-gray-500" data-testid="word-count">
            {wordCount} words {wordCount >= 300 && wordCount <= 1000 && '✓'}
          </span>
        </div>
        <Textarea
          id="story-content"
          placeholder="Start writing your flash fiction story here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={15}
          className="font-serif text-base leading-relaxed"
          data-testid="story-content-textarea"
        />
        <p className="text-xs text-gray-500">
          Flash fiction is typically 300-1000 words. Current: {wordCount} words
        </p>
      </div>

      <Button
        onClick={handleSave}
        disabled={loading}
        className="w-full bg-green-600 hover:bg-green-700"
        data-testid="save-story-button"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="mr-2 h-4 w-4" />
            Save Story
          </>
        )}
      </Button>
    </div>
  );
};

export default StoryEditor;
